import * as Sentry from "@sentry/react";
import type { ReactElement, ReactNode } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { Button, resolveTheme } from "@jackedlogs/ui";
import {
  radius,
  scrubBreadcrumb,
  scrubEvent,
  spacing,
  typography,
} from "@jackedlogs/shared";

// Crash visibility for web-rnw — mirrors apps/mobile/src/util/crashReporting.ts
// (same @sentry init shape, same ONE shared PII scrub from
// @jackedlogs/shared crashScrub, so the zero-PII posture can't drift). Until
// VITE_SENTRY_DSN is provisioned the SDK stays inert (init early-returns),
// exactly like mobile shipped before its DSN landed — but the ErrorBoundary
// below already turns a React #130 white-screen into a visible, reload-able
// fallback, DSN or not. [[feedback_crash_visibility_before_recovery]].

const DSN = import.meta.env.VITE_SENTRY_DSN;

let initialized = false;

export function initWebCrashReporting(): void {
  if (!DSN) return;
  Sentry.init({
    dsn: DSN,
    release: `web-rnw@${__APP_VERSION__}`,
    // Vite's build-time parallel to RN's __DEV__.
    environment: import.meta.env.DEV ? "dev" : "production",
    sendDefaultPii: false,
    // Crash-only first ship, matching mobile — perf tracing parked.
    tracesSampleRate: 0,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
  initialized = true;
}

// Mirrors apps/mobile/src/util/crashReporting.ts's captureRecoveryEvent —
// same info-level breadcrumb for the edit/repeat draft-conflict path, so a
// stuck web session is as traceable as a mobile one. No-ops with no DSN,
// same as init above.
export function captureRecoveryEvent(
  name: string,
  extra: Record<string, unknown>,
): void {
  if (!initialized) return;
  Sentry.captureMessage(name, { level: "info", extra });
}

// The fallback is provider-independent ON PURPOSE: the WebThemeProvider (or
// anything above it) may be the thing that crashed, so the theme is resolved
// with the PURE resolveTheme() (no React context) + prefers-color-scheme,
// never ThemeContext. The shared <Button> primitive takes that theme as a
// prop, so reusing it here stays context-free too.
const prefersDark =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;
const theme = resolveTheme(prefersDark);
const p = theme.palette;

// RW-22 — the fallback SHOWS what broke. Sentry hands the boundary the error
// and nothing here was reading it, so the screen said "something went wrong"
// and threw the one useful fact away. With no DSN provisioned yet
// (VITE_SENTRY_DSN, see init above) that fallback text was the ONLY channel a
// web crash had, which turned every report into "open devtools and read me
// the console" — a round trip per crash, paid by the person who already did
// the work of finding it. [[feedback_crash_visibility_before_recovery]]
//
// Deliberately not gated behind DEV: the people hitting this ARE the testers,
// and a crash they can quote is worth more than a tidier crash they can't.
function CrashFallback({ error }: { error?: unknown }): ReactElement {
  const { t } = useTranslation();
  const detail =
    error instanceof Error
      ? [error.message, error.stack?.split('\n').slice(1, 4).join('\n')]
          .filter(Boolean)
          .join('\n')
      : error !== undefined && error !== null
        ? String(error)
        : null;
  return (
    <View
      // role=alert → an assertive live region so a screen-reader / magnifier
      // user is told the crash screen appeared, not left to notice it.
      accessibilityRole="alert"
      style={{
        flex: 1,
        backgroundColor: p.bg,
        alignItems: "center",
        justifyContent: "center",
        padding: spacing.s6,
      }}
    >
      <View
        style={{
          maxWidth: 420,
          width: "100%",
          backgroundColor: p.surface,
          borderRadius: radius.r3,
          borderWidth: 1,
          borderColor: p.borderStrong,
          padding: spacing.s6,
          gap: spacing.s4,
        }}
      >
        <Text
          accessibilityRole="header"
          style={{
            color: p.ink,
            fontSize: typography.sizes.lg,
            fontWeight: "700",
          }}
        >
          {t("errors.generic")}
        </Text>
        <Text
          style={{
            color: p.ink2,
            fontSize: typography.sizes.md,
            lineHeight: typography.sizes.md * typography.leading.base,
          }}
        >
          {t("web.crash.body")}
        </Text>
        {detail !== null ? (
          // selectable: the whole point is that it can be copied into a bug
          // report. Monospace because it is a stack, and scrollable because a
          // long frame must not push the reload button off the card.
          <View
            style={{
              backgroundColor: p.surface2,
              borderRadius: radius.r2,
              borderWidth: 1,
              borderColor: p.border,
              maxHeight: 180,
              padding: spacing.s3,
            }}
          >
            <Text
              selectable
              style={{
                color: p.ink2,
                fontFamily: "monospace",
                fontSize: typography.sizes.sm,
                lineHeight: typography.sizes.sm * typography.leading.base,
              }}
            >
              {detail}
            </Text>
          </View>
        ) : null}
        <Button
          theme={theme}
          variant="secondary"
          label={t("web.crash.reload")}
          onPress={() => window.location.reload()}
          style={{ alignSelf: "flex-start" }}
        />
      </View>
    </View>
  );
}

/** Wraps the app so a render crash is captured by Sentry (when a DSN is set)
 *  AND shown as a reload-able fallback instead of a blank page. */
export function WebCrashBoundary({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  return (
    // Sentry hands the fallback the error; taking it as a function instead of
    // an element is what gives us access to it at all.
    <Sentry.ErrorBoundary fallback={({ error }) => <CrashFallback error={error} />}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
